<script setup lang="ts">
import { useCorporateNavigation } from '@corporate/composables'
import { useFocusTrap } from '@corporate/composables/useFocusTrap'
import { FooterSocialList } from '@gro-up/ui/components'
import { useDomainMapper } from '@gro-up/ui/composables'
import { Button, Logo, CustomNuxtLink, DomainLink, SvgComponent, DomainLabel, Typography } from '@gro-up/ui/primitives'
import { useScrollLock } from '@vueuse/core'
import { ref, watch } from 'vue'

import { useRoute } from '#app'

const model = defineModel<boolean>({ default: false })
const menuRef = ref<HTMLElement | null>(null)
const route = useRoute()

const isLocked = useScrollLock(import.meta.client ? document.body : null)
watch(model, val => (isLocked.value = val), { immediate: true })

const { subHome, primaryLinks, homeLinks, socialMedia, domain, subDomain, search } = useCorporateNavigation()

const { getDomainLabel } = useDomainMapper()

useFocusTrap(model, menuRef)

function getParts(el: Element) {
  return {
    upper: el.querySelector<HTMLElement>('.slide-upper')!,
    lower: el.querySelector<HTMLElement>('.slide-lower')!,
  }
}

function onBeforeEnter(el: Element) {
  const { upper, lower } = getParts(el)
  upper.style.transform = 'translateY(-100%)'
  lower.style.transform = 'translateY(100%)'
}

function onEnter(el: Element, done: () => void) {
  void (el as HTMLElement).offsetHeight // force reflow zodat de transitie fireert
  const { upper, lower } = getParts(el)
  upper.style.transform = ''
  lower.style.transform = ''
  upper.addEventListener('transitionend', done, { once: true })
}

function onLeave(el: Element, done: () => void) {
  const { upper, lower } = getParts(el)
  upper.style.transform = 'translateY(-100%)'
  lower.style.transform = 'translateY(100%)'
  upper.addEventListener('transitionend', done, { once: true })
}
</script>

<template>
  <Transition
    :css="false"
    @before-enter="onBeforeEnter"
    @enter="onEnter"
    @leave="onLeave">
    <div v-show="model" ref="menuRef" class="menu-overlay">
      <div class="menu-scroll">
        <div class="slide-upper bg-head-office-20">
          <section :class="['upper', subHome?.mainItems || subHome?.subItems ? 'pb-13' : '']">
            <div class="py-4 flex flex-row justify-between">
              <div class="flex gap-1 items-center">
                <CustomNuxtLink to="/" aria-label="Home">
                  <Logo class="h-6 md:h-9" variant="dark" />
                </CustomNuxtLink>
                <CustomNuxtLink v-if="domain" :to="domain?.url" :aria-label="domain.domain">
                  <DomainLabel v-if="domain?.domain" class="h-6 md:h-9" :domain="domain.domain" />
                </CustomNuxtLink>
                <CustomNuxtLink
                  v-if="subDomain?.title"
                  :to="subDomain.url"
                  :aria-label="subDomain.title"
                  class="text-[14px] font-bold text-primary-100 leading-none">
                  {{ subDomain.title }}
                </CustomNuxtLink>
              </div>

              <div class="flex gap-4 items-center justify-end">
                <CustomNuxtLink
                  v-if="search"
                  :to="search.url"
                  class="w-[18px] h-[18px] text-primary-80 hover:text-accent-100 transition-colors">
                  <SvgComponent icon="search" class="h-[18px] w-[18px]" />
                </CustomNuxtLink>
                <button
                  class="group hover:cursor-pointer transition-colors duration-300"
                  aria-label="Sluit menu"
                  @click="model = !model">
                  <div class="flex flex-col justify-between items-center w-[18px] h-[18px]">
                    <div
                      class="w-full h-0.5 transition-all duration-200 ease-smooth origin-center rotate-45 translate-y-[8px] bg-primary-100" />
                    <div class="w-full h-0.5 transition-all duration-200 ease-smooth opacity-0 bg-primary-100" />
                    <div
                      class="w-full h-0.5 transition-all duration-200 ease-smooth origin-center -rotate-45 -translate-y-[8px] bg-primary-100" />
                  </div>
                </button>
              </div>
            </div>
            <div class="flex flex-col gap-[15px] mt-[15px] md:mt-2 md:gap-2">
              <CustomNuxtLink
                v-if="domain"
                :to="domain?.url"
                :aria-label="domain.domain"
                :class="{ 'text-theme-100': domain.currentPage }"
                class="flex items-center gap-1">
                <SvgComponent icon="house-icon" class="h-4" />
                <Typography as="span" variant="header-3" class="font-bold">{{
                  getDomainLabel(domain?.domain)
                }}</Typography>
              </CustomNuxtLink>
              <ul v-if="subHome?.mainItems" class="flex flex-col gap-5 md:gap-2">
                <li v-for="(item, index) in subHome.mainItems" :key="`sitenav-mainItem-${index}` + item.title">
                  <CustomNuxtLink :to="item.url" :class="{ 'text-theme-100': item.currentPage }">
                    <Typography as="span" variant="header-3" class="font-bold">{{ item.title }}</Typography>
                  </CustomNuxtLink>
                </li>
              </ul>
              <ul v-if="subHome?.subItems" class="flex flex-col gap-[15px] md:gap-2" aria-label="Sub menu mobiel">
                <li v-for="(item, index) in subHome.subItems" :key="`sitenav-subItem-${index}` + item.title">
                  <CustomNuxtLink
                    :to="item.url"
                    :class="{ 'text-theme-100': item.currentPage }"
                    class="text-primary-60">
                    <span class="text-body-medium flex items-center gap-1">
                      {{ item.title }}
                      <svg
                        v-if="item.external"
                        width="8"
                        height="8"
                        viewBox="0 0 8 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M1.38086 1.38135H6.61895V6.61944"
                          stroke="currentColor"
                          stroke-width="1.25"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                        <path
                          d="M1.38086 6.61944L6.61895 1.38135"
                          stroke="currentColor"
                          stroke-width="1.25"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </span>
                  </CustomNuxtLink>
                </li>
              </ul>
              <div v-if="subHome?.buttons" class="flex flex-wrap gap-1">
                <Button
                  v-for="(item, index) in subHome.buttons"
                  :key="`sitenav-button-${index}`"
                  as="NuxtLink"
                  variant="primary"
                  size="sm"
                  class="grow shrink h-10 text-caption-bold basis-auto only:grow-0 !bg-[#352942] !border-[#352942] !text-white hover:!bg-[#2d2336] hover:!border-[#2d2336]"
                  :to="item.url">
                  {{ item.title }}
                </Button>
              </div>
            </div>
          </section>
        </div>

        <div class="slide-lower bg-primary-90 rounded-xl pt-10 pb-16 -mt-3 flex-1">
          <section class="lower relative">
            <img src="/illustrations/navigation-gruppets-small.webp" width="131" height="239" class="absolute top-0 right-8" alt="" >

            <ul class="flex flex-col gap-2">
              <li
                v-for="(item, index) in primaryLinks"
                :key="`primary-${index}`"
                class="text-white"
                :class="item.type === 'corporateSubhomeNavigationLink'
                  ? (item.domain === 'youth-support' ? 'pl-6 py-[6px]' : 'pl-2 py-[6px]')
                  : ''">
                <DomainLink
                  v-if="item.type === 'corporateSubhomeNavigationLink'"
                  :domain="item.domain ?? 'head-office'"
                  variant="header-3"
                  inverted
                  :active="item.domain === domain?.domain"
                  :url="item.url ?? ''" />
                <CustomNuxtLink
                  v-else
                  :to="item.url ?? ''"
                  class="header-5 font-bold tracking-tight transition-colors hover:text-accent-100"
                >{{ item.title }}</CustomNuxtLink
                >
              </li>
            </ul>
            <ul class="mt-8">
              <li v-for="(item, index) in homeLinks" :key="`homelinks-${index}`">
                <CustomNuxtLink :to="item.url" class="text-body-medium text-primary-40">
                  {{ item.title }}
                </CustomNuxtLink>
              </li>
            </ul>

            <FooterSocialList class="mt-10 text-primary-40" variant="currentColor" :socials="socialMedia" />

            <CustomNuxtLink v-if="route.path !== '/'" to="/" class="text-body-medium text-primary-40 flex items-center gap-3 mt-10">
              <span class="w-10 h-10 rounded-md bg-primary-100 text-white grid place-items-center">
                <SvgComponent icon="chevron-left" />
              </span>
              naar de homepage
            </CustomNuxtLink>
          </section>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
@reference "@gro-up/ui/main.css";

.menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  overflow: hidden;
}

.menu-scroll {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
}

.slide-upper,
.slide-lower {
  transition: transform 0.5s cubic-bezier(0.76, 0, 0.24, 1);
}

.upper,
.lower {
  @apply px-4;
}
</style>

